(function () {
  'use strict';

  if (!window.customElements || customElements.get('chupps-shoppable-feed')) return;

  class ChuppsShoppableFeed extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-video-dialog]') || this.querySelector('dialog.chupps-shoppable-videos__dialog');
      this.feed = this.querySelector('[data-video-feed]');
      this.slides = Array.from(this.querySelectorAll('[data-video-slide]'));
      this.railPlayers = Array.from(this.querySelectorAll('[data-rail-video]'));
      this.rail = this.querySelector('[data-video-rail]');
      this.activeIndex = -1;
      this.currentSlide = null;
      this.preparedPlayers = new WeakSet();
      this.returnFocus = null;
      this.controlsHideTimer = null;
      this.keyboardInteraction = false;
      this.prefersReducedMotion = Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      this.onClick = this.onClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onRailScroll = this.onRailScroll.bind(this);
      this.onRailPlayerPlay = this.onRailPlayerPlay.bind(this);
      this.onPlayerState = this.onPlayerState.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onDialogInteraction = this.onDialogInteraction.bind(this);
      this.onDialogFocusout = this.onDialogFocusout.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.railPlayers.forEach((player) => player.addEventListener('playing', this.onRailPlayerPlay));
      this.dialog?.addEventListener('close', this.onClose);
      this.dialog?.addEventListener('cancel', this.onCancel);
      this.dialog?.addEventListener('keydown', this.onKeydown);
      this.dialog?.addEventListener('click', this.onDialogInteraction);
      this.dialog?.addEventListener('focusin', this.onDialogInteraction);
      this.dialog?.addEventListener('focusout', this.onDialogFocusout);
      this.rail?.addEventListener('scroll', this.onRailScroll, { passive: true });
      this.slides.forEach((slide) => {
        const player = slide.querySelector('[data-video-player]');
        player?.addEventListener('play', this.onPlayerState);
        player?.addEventListener('pause', this.onPlayerState);
        player?.addEventListener('volumechange', this.onPlayerState);
        if (player) this.updatePlayerControls(player);
      });
      this.observeRailVideos();
      this.observeSlides();
      this.updateNavigation();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.dialog?.removeEventListener('close', this.onClose);
      this.dialog?.removeEventListener('cancel', this.onCancel);
      this.dialog?.removeEventListener('keydown', this.onKeydown);
      this.dialog?.removeEventListener('click', this.onDialogInteraction);
      this.dialog?.removeEventListener('focusin', this.onDialogInteraction);
      this.dialog?.removeEventListener('focusout', this.onDialogFocusout);
      this.clearControlsHideTimer();
      this.rail?.removeEventListener('scroll', this.onRailScroll);
      this.feed?.removeEventListener('scroll', this.onScroll);
      this.slideObserver?.disconnect();
      this.railVideoObserver?.disconnect();
      this.preloadObserver?.disconnect();
      this.railPlayers?.forEach((player) => {
        player.removeEventListener('playing', this.onRailPlayerPlay);
        player.pause();
      });
      this.slides?.forEach((slide) => {
        const player = slide.querySelector('[data-video-player]');
        player?.removeEventListener('play', this.onPlayerState);
        player?.removeEventListener('pause', this.onPlayerState);
        player?.removeEventListener('volumechange', this.onPlayerState);
        player?.pause();
      });
    }

    observeRailVideos() {
      if (!this.railPlayers.length || !('IntersectionObserver' in window)) return;
      this.preloadObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.prefersReducedMotion || document.hidden) return;
          const player = entry.target;
          this.preparePlayer(player);
          const index = this.railPlayers.indexOf(player);
          this.prepareSlide(index);
          this.preloadObserver.unobserve(player);
        });
      }, { rootMargin: '1200px 300px', threshold: 0 });
      this.railPlayers.forEach((player) => this.preloadObserver.observe(player));
      this.railVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const player = entry.target;
          if (!entry.isIntersecting || entry.intersectionRatio < 0.01 || this.dialog?.open || document.hidden || this.prefersReducedMotion) {
            player.pause();
            return;
          }
          this.preparePlayer(player);
          const playback = player.play();
          if (playback && typeof playback.catch === 'function') playback.catch(() => {});
        });
      }, { threshold: [0, 0.01] });
      this.railPlayers.forEach((player) => this.railVideoObserver.observe(player));
    }

    loadRailPoster(player) {
      const poster = player.dataset.poster;
      if (poster && player.poster !== poster) player.poster = poster;
    }

    preparePlayer(player) {
      if (!player || this.preparedPlayers.has(player)) return;
      this.preparedPlayers.add(player);
      this.loadRailPoster(player);
      player.preload = 'auto';
      if (player.readyState === 0) player.load();
    }

    prepareSlide(index) {
      this.preparePlayer(this.slides[index]?.querySelector('[data-video-player]'));
    }

    onRailPlayerPlay(event) {
      event.currentTarget.classList.add('is-playing');
    }

    syncRailVideos() {
      const canPlay = !this.dialog?.open && !document.hidden && !this.prefersReducedMotion;
      this.railPlayers.forEach((player) => {
        const rect = player.getBoundingClientRect();
        const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
        const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        const visibleRatio = rect.width && rect.height ? width * height / (rect.width * rect.height) : 0;
        if (!canPlay || visibleRatio < 0.01) {
          player.pause();
          return;
        }
        this.preparePlayer(player);
        const playback = player.play();
        if (playback && typeof playback.catch === 'function') playback.catch(() => {});
      });
    }

    onVisibilityChange() {
      this.syncRailVideos();
    }

    observeSlides() {
      if (!this.feed || !this.slides.length) return;
      if (!('IntersectionObserver' in window)) {
        this.feed.addEventListener('scroll', this.onScroll, { passive: true });
        return;
      }
      this.slideObserver = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];
        if (visible && this.dialog?.open) this.activateSlide(visible.target);
      }, { root: this.feed, threshold: [0.5] });
      this.slides.forEach((slide) => this.slideObserver.observe(slide));
    }

    onClick(event) {
      const openLink = event.target.closest('[data-video-open]');
      if (openLink && this.contains(openLink)) {
        event.preventDefault();
        this.openVideo(Number(openLink.dataset.videoIndex), openLink);
        return;
      }

      if (event.target.closest('[data-video-close]')) {
        this.dialog?.close();
        return;
      }

      const mute = event.target.closest('[data-video-mute]');
      if (mute) {
        const player = mute.closest('[data-video-slide]')?.querySelector('[data-video-player]');
        if (player) player.muted = !player.muted;
        return;
      }

      if (event.target.closest('[data-video-previous]')) {
        this.moveTo(this.activeIndex - 1);
        return;
      }

      if (event.target.closest('[data-video-next]')) {
        this.moveTo(this.activeIndex + 1);
        return;
      }

      const railButton = event.target.closest('[data-rail-previous], [data-rail-next]');
      if (railButton && this.rail) {
        const card = this.rail.querySelector('[data-video-card]');
        const distance = (card?.getBoundingClientRect().width || 230) + 12;
        this.rail.scrollBy({ left: railButton.hasAttribute('data-rail-next') ? distance * 2 : -distance * 2, behavior: 'smooth' });
        return;
      }

      if (event.target === this.dialog) this.dialog.close();
    }

    onKeydown(event) {
      if (this.dialog?.open) {
        this.keyboardInteraction = true;
        this.revealControls();
      }
      if (!this.dialog?.open || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      if (event.target.closest('button, a, video, input, select, textarea')) return;
      event.preventDefault();
      this.moveTo(this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
    }

    onScroll() {
      if (!this.feed || !this.dialog?.open || !this.slides.length) return;
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => {
        const feedRect = this.feed.getBoundingClientRect();
        const center = feedRect.top + feedRect.height / 2;
        const nearest = this.slides.reduce((best, slide) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.top + rect.height / 2 - center);
          return distance < best.distance ? { slide, distance } : best;
        }, { slide: null, distance: Infinity }).slide;
        if (nearest) this.activateSlide(nearest);
      });
    }

    onRailScroll() {
      this.updateNavigation();
    }

    onPlayerState(event) {
      this.updatePlayerControls(event.currentTarget);
      this.scheduleControlsHide();
    }

    onDialogInteraction(event) {
      if (!this.dialog?.open) return;
      if (event.type === 'focusin' && !this.keyboardInteraction) return;
      if (event.type !== 'focusin') this.keyboardInteraction = false;
      this.revealControls();
    }

    onDialogFocusout() {
      window.requestAnimationFrame(() => this.scheduleControlsHide());
    }

    revealControls() {
      if (!this.dialog?.open) return;
      this.clearControlsHideTimer();
      this.dialog.classList.remove('is-controls-hidden');
      this.scheduleControlsHide();
    }

    scheduleControlsHide() {
      this.clearControlsHideTimer();
      if (!this.dialog?.open || (this.keyboardInteraction && this.dialog.querySelector('.chupps-shoppable-videos__dialog-controls :focus, .chupps-shoppable-videos__player-controls :focus'))) return;
      this.controlsHideTimer = window.setTimeout(() => {
        this.dialog?.classList.add('is-controls-hidden');
        this.controlsHideTimer = null;
      }, 2400);
    }

    clearControlsHideTimer() {
      if (this.controlsHideTimer !== null) {
        window.clearTimeout(this.controlsHideTimer);
        this.controlsHideTimer = null;
      }
    }

    updatePlayerControls(player) {
      const slide = player.closest('[data-video-slide]');
      const mute = slide?.querySelector('[data-video-mute]');
      const muteLabel = this.querySelector('[data-video-mute-label]')?.textContent || '';
      const unmuteLabel = this.querySelector('[data-video-unmute-label]')?.textContent || '';
      if (mute) {
        mute.setAttribute('aria-pressed', String(!player.muted));
        mute.setAttribute('aria-label', player.muted ? unmuteLabel : muteLabel);
      }
    }

    openVideo(index, opener) {
      if (!this.dialog || !this.slides[index]) return;
      this.returnFocus = opener;
      this.activeIndex = index;
      this.keyboardInteraction = false;
      this.clearControlsHideTimer();
      this.dialog.classList.add('is-controls-hidden');
      if (!this.dialog.open) this.dialog.showModal();
      this.syncRailVideos();
      this.updateNavigation();
      this.activateSlide(this.slides[index]);
      window.requestAnimationFrame(() => {
        this.slides[index].scrollIntoView({ block: 'start', behavior: 'auto' });
        this.activateSlide(this.slides[index]);
        this.feed?.focus({ preventScroll: true });
      });
    }

    activateSlide(slide) {
      if (this.currentSlide === slide) return;
      this.currentSlide = slide;
      const index = Number(slide.dataset.videoIndex);
      this.activeIndex = index;
      this.prepareSlide(index);
      this.slides.forEach((candidate, candidateIndex) => {
        const player = candidate.querySelector('[data-video-player]');
        if (!player) return;
        if (candidateIndex === index && this.dialog?.open) {
          const playback = player.play();
          if (playback && typeof playback.catch === 'function') playback.catch(() => {});
        } else {
          player.pause();
        }
      });
      this.prepareSlide(index + 1);
      this.prepareSlide(index - 1);
      this.updateNavigation();
    }

    moveTo(index) {
      const target = this.slides[index];
      if (!target || !this.feed) return;
      this.prepareSlide(index);
      this.activeIndex = index;
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      this.updateNavigation();
    }

    updateNavigation() {
      const previous = this.querySelector('[data-video-previous]');
      const next = this.querySelector('[data-video-next]');
      if (previous) previous.disabled = this.activeIndex <= 0;
      if (next) next.disabled = this.activeIndex < 0 || this.activeIndex >= this.slides.length - 1;
      const railPrevious = this.querySelector('[data-rail-previous]');
      const railNext = this.querySelector('[data-rail-next]');
      if (railPrevious) railPrevious.disabled = !this.rail || this.rail.scrollLeft <= 1;
      if (railNext && this.rail) railNext.disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 1;
    }

    onCancel(event) {
      event.preventDefault();
      this.dialog?.close();
    }

    onClose() {
      this.clearControlsHideTimer();
      this.dialog?.classList.remove('is-controls-hidden');
      this.slides.forEach((slide) => slide.querySelector('[data-video-player]')?.pause());
      this.activeIndex = -1;
      this.currentSlide = null;
      this.updateNavigation();
      this.syncRailVideos();
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }
  }

  customElements.define('chupps-shoppable-feed', ChuppsShoppableFeed);
})();
